// src/pages/AppliAdmin/ContactForm/components/ContactMessageCard.jsx
import React from "react";
import {
  FaClock,
  FaCopy,
  FaEnvelope,
  FaEnvelopeOpen,
  FaTrash,
  FaUser,
} from "react-icons/fa";
import {
  cleanMessage,
  formatDateTimeFR,
  formatRelativeDate,
} from "../contactFormUtils";

export default function ContactMessageCard({
  message,
  deletingId,
  markingId,
  onDelete,
  onToggleRead,
}) {
  const isRead = Boolean(message.read_message);

  const copyEmail = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!message.email) return;

    try {
      await navigator.clipboard.writeText(message.email);
    } catch {
      console.warn("Impossible de copier l'email");
    }
  };

  return (
    <article
      onClick={(event) => event.stopPropagation()}
      className={`rounded-[24px] border p-4 transition ${
        isRead
          ? "border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900"
          : "border-indigo-200 bg-indigo-50/70 shadow-sm dark:border-indigo-900/60 dark:bg-indigo-950/30"
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${
            isRead
              ? "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300"
              : "bg-indigo-600 text-white"
          }`}
        >
          {isRead ? <FaEnvelopeOpen /> : <FaEnvelope />}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <ReadToggle
              read={isRead}
              disabled={markingId === message.id}
              onToggle={() => onToggleRead(message)}
            />

            <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 dark:text-slate-400">
              <FaClock />
              {formatRelativeDate(message.created_at)}
            </span>
          </div>

          <h3 className="mt-2 flex items-center gap-2 text-base font-bold text-slate-900 dark:text-white">
            <FaUser className="text-xs text-slate-400" />
            {message.name || "Nom non renseigné"}
          </h3>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="break-all rounded-xl bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 ring-1 ring-slate-200 dark:bg-slate-950 dark:text-slate-200 dark:ring-slate-800">
              {message.email || "Email non renseigné"}
            </span>

            {message.email ? (
              <button
                type="button"
                onClick={copyEmail}
                className="inline-flex items-center gap-1 rounded-xl bg-slate-100 px-2.5 py-1.5 text-xs font-bold text-slate-600 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                <FaCopy />
                Copier
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 text-sm leading-relaxed text-slate-700 dark:border-slate-800 dark:bg-slate-950/60 dark:text-slate-200">
        <p className="whitespace-pre-wrap">
          {cleanMessage(message.message) || "—"}
        </p>
      </div>

      <div className="mt-4 flex flex-col gap-3 border-t border-slate-200 pt-4 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-xs text-slate-500 dark:text-slate-400">
          {formatDateTimeFR(message.created_at)}
        </div>

        <button
          type="button"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onDelete(message.id);
          }}
          disabled={deletingId === message.id}
          className="inline-flex items-center justify-center gap-2 rounded-2xl border border-red-100 bg-red-50 px-3.5 py-2 text-sm font-bold text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300"
        >
          <FaTrash />
          Supprimer
        </button>
      </div>
    </article>
  );
}

export function ReadToggle({ read, disabled, onToggle }) {
  return (
    <button
      type="button"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onToggle?.();
      }}
      disabled={disabled}
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold transition disabled:cursor-not-allowed disabled:opacity-60 ${
        read
          ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-950/70 dark:text-emerald-300"
          : "bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-950/70 dark:text-amber-300"
      }`}
      title={read ? "Basculer en non lu" : "Basculer en lu"}
    >
      {read ? (
        <>
          <FaEnvelopeOpen />
          Lu
        </>
      ) : (
        <>
          <FaEnvelope />
          Non lu
        </>
      )}
    </button>
  );
}